import React, { createContext, useContext, useState, useEffect } from "react";

import Container from "@cloudscape-design/components/container";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Spinner from "@cloudscape-design/components/spinner";
import Box from "@cloudscape-design/components/box";

import axios from "axios"

import { shouldAuth, tokens } from "./auth"
import { sanitizeNavigationURL } from "../utils/url-validation"

// Session storage key to track explicit logout
const LOGGED_OUT_KEY = "userLoggedOut"

const AuthContext = createContext()
const useAuthContext = () => useContext(AuthContext)

const AuthContextProvider = ({children}) => {
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState({})
  const [error, setError] = useState(null)
  const [settings, setSettings] = useState(null)
  const [isLoggedOut, setIsLoggedOut] = useState(false)

  useEffect(() => {
    let cancelled = false
    
    const initAuth = async () => {
      if (cancelled) return
      
      // Check if user explicitly logged out - if so, show logged out screen
      const wasLoggedOut = sessionStorage.getItem(LOGGED_OUT_KEY) === "true"
      
      if (wasLoggedOut) {
        // User explicitly logged out, show logged out screen
        try {
          const resp = await axios.get("/settings.json")
          if (!cancelled) {
            setSettings(resp.data)
          }
        } catch (err) {
          console.error("Failed to load settings:", err)
        }
        if (!cancelled) {
          setIsLoggedOut(true)
          setLoading(false)
        }
        return
      }

      if (shouldAuth()) {
        // Store only pathname to prevent XSS via full URL manipulation
        localStorage.setItem("url", window.location.pathname)
        
        axios.get("/settings.json").then(resp => {
          if (cancelled) return
          const { cognitoUrl } = resp.data
          setSettings(resp.data)
 
          window.location.href = cognitoUrl
        }).catch(error => {
          console.error("Failed to load authentication configuration:", error)
          if (!cancelled) {
            setError("Authentication configuration failed to load. Please contact support.")
            setLoading(false)
          }
        })
      } else {
        // Load settings first, then set token
        try {
          const resp = await axios.get("/settings.json")
          if (cancelled) return
          setSettings(resp.data)
          
          const { idToken } = tokens()
          setToken(idToken)
          setLoading(false)
    
          const location = localStorage.getItem("url")
          if (location) {
            localStorage.removeItem("url")
            
            // Validate URL before navigation to prevent XSS
            const safeURL = sanitizeNavigationURL(location, '/')
            if (safeURL !== location) {
              console.warn('Potentially malicious URL sanitized during authentication redirect:', location)
            }
            window.location.href = safeURL
          }
        } catch (error) {
          console.error("Failed to load settings:", error)
          if (!cancelled) {
            setError("Failed to load application settings.")
            setLoading(false)
          }
        }
      }
    }

    initAuth()
    
    return () => { cancelled = true }
  }, []) // Empty dependency array - only run once on mount

  // Loading screen
  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <SpaceBetween size="l">
          <img 
            src="/aws-logo.png" 
            alt="AWS Logo" 
            style={{ height: '60px', width: 'auto', margin: '0 auto' }}
          />
          <Box variant="h1" fontSize="heading-xl">
            WW FSI Document Management - REGISTRY
          </Box>
          {error ? (
            <Box variant="p" color="text-status-error">
              {error}
            </Box>
          ) : (
            <SpaceBetween size="m" alignItems="center">
              <Spinner size="large" />
              <Box variant="p">
                Authenticating your access via Midway...
              </Box>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Box>
    )
  }

  // Logged out screen - shown when user explicitly clicked logout
  if (isLoggedOut) {
    const handleLogin = () => {
      // Clear the logged out flag
      sessionStorage.removeItem(LOGGED_OUT_KEY)
      
      // Redirect to Cognito login
      if (settings && settings.cognitoUrl) {
        window.location.href = settings.cognitoUrl
      } else {
        // Fallback: reload to trigger auth flow
        window.location.reload()
      }
    }

    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <Container>
          <SpaceBetween size="l">
            <img 
              src="/aws-logo.png" 
              alt="AWS Logo" 
              style={{ height: '60px', width: 'auto', margin: '0 auto' }}
            />
            <Box variant="h1" fontSize="heading-xl">
              WW FSI Document Management - REGISTRY
            </Box>
            <Box padding={{ top: "l", bottom: "l" }}>
              <SpaceBetween size="m">
                <Box variant="h2" fontSize="heading-l">
                  You have been logged out
                </Box>
                <Box variant="p" color="text-body-secondary">
                  Thank you for using KMSDID. Click below to log back in.
                </Box>
                <Button variant="primary" onClick={handleLogin}>
                  Log In with Midway
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Container>
      </Box>
    )
  }

  const logout = () => {
    // Clear local storage tokens
    localStorage.removeItem("accessToken")
    localStorage.removeItem("idToken")
    localStorage.removeItem("expiry")
    localStorage.removeItem("url")
    
    // Set flag to indicate explicit logout (use sessionStorage so it clears on browser close)
    sessionStorage.setItem(LOGGED_OUT_KEY, "true")
    
    // Set logged out state to show the logged out screen
    setIsLoggedOut(true)
    setToken({})
  }

  return (
    <AuthContext.Provider value={{
      token: token, 
      error: error, 
      logout: logout,
      settings: settings
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContextProvider 

export {
  useAuthContext,
  AuthContext
}